import { Box, Button, Card, Grid, Typography, useTheme } from "@mui/material";
import LockIcon from '@mui/icons-material/Lock'
import UnlockIcon from '@mui/icons-material/LockOpen'
import UndoIcon from '@mui/icons-material/Undo';
import CheckIcon from '@mui/icons-material/Check';
import { push } from "connected-react-router";
import { crudGetList, ReactAdminComponentPropsWithId, ReduxState, useEditContext, useGetOne, UseGetOneHookValue, useRecordContext, useWhyDidYouUpdate } from "ra-core";
import { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Content } from "@/components/content/Content";
import { useAllDatabasesSource, useDatabaseSelectCard } from "@/components/databases/DatabaseSelectCard";
import Edit from "@/controller/Edit";
import { DatabaseResponseDto } from "@/gen/api/backup";
import { DatabaseResponseDto as DbStatus, EncryptionSettingChangesResponseDto, UpgradeStatus } from "@/gen/api/encrypt";
import { OdResourcePageProps } from "@/types/resource";
import { decodeHashid, genUrlWithHashid } from "@/utils/hashid";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import ESChangeViewer from "@/components/databases/ESChangeViewer";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { RecPatch } from "@/dataProvider/tools";
import { EncryptedDatabasesContextProvider, useEncryptedDatabasesContext } from "@/components/databases/useEncryptedDatabases";
import { getUserProfile } from "@/reducers/userProfile";
import { OnDataFeature } from "@/gen/api/user";
import { Redirect } from "react-router-dom";
import Confirm from "@/components/widgets/Confirm";
import { ESCRenameHintContextProvider } from "@/components/databases/ESCRenameHintContext";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { MANAGE_DATABASE_OPERATIONS } from "@/constants";
import { nonSqlDbTypes } from "@/components/obfuscation/defs";


const DatabaseEncryptionSettingChange: FC<ReactAdminComponentPropsWithId> = (props) => {
  const { resource, match, ...rest } = props
  const id = decodeHashid(match ? (match.params as any)['id'] : '')

  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(crudGetList('databases', pages(), sorts(), {}))
  }, [dispatch])

  const {currDb, escStatusReady, hasDriverFeature} = useSelector<ReduxState, any>(store => ({
    currDb: store.admin.resources['databases']?.data[id] as DatabaseResponseDto,
    escStatusReady: !!store.admin.resources['db-esc-status'],
    hasDriverFeature: 
      getUserProfile(store) 
      ? getUserProfile(store)?.features?.some(f => f.featureId === OnDataFeature.OnDataDriver) as boolean
      : true,
  }))
  
  if (!hasDriverFeature) { return <Redirect to="/" /> }
  if (!currDb || !escStatusReady) { return null }
  if (nonSqlDbTypes.has(currDb.databaseType)) { return <Redirect to="/encryption/dashboard" /> }

  return (
    <Edit id={id} basePath="" resource={resource} mutationMode="pessimistic" >
      <DbEscStatusContextProvider id={id}>
        <EncryptedDatabasesContextProvider>
          <DatabaseEncryptionSettingChangeBody {...props} databaseInfo={currDb} />
        </EncryptedDatabasesContextProvider>
      </DbEscStatusContextProvider>
    </Edit>
  )
}

const useDataSource = () => {
  const dbs = useAllDatabasesSource()
  const ds = useMemo(() => dbs.filter(db => !nonSqlDbTypes.has((db as DatabaseResponseDto)?.databaseType!)), [dbs])
  return () => ds
}

const DatabaseEncryptionSettingChangeBody: FC<OdResourcePageProps & {databaseInfo:DatabaseResponseDto}> = ({
  options,
  resource,
  databaseInfo,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_DATABASE_OPERATIONS])
  const dataSource = useDataSource()
  const DatabaseSelectCard = useDatabaseSelectCard(dataSource)
  const [openRollback, setOpenRollback] = useState(false)
  
  const theme = useTheme()
  const dispatch = useDispatch()
  const handleChange = useCallback((e: any, chosenDb?: DatabaseResponseDto) => {
    if (chosenDb?.databaseId) {
      dispatch(push(
        genUrlWithHashid(`/databases/$0/encryption-setting-change`, chosenDb.databaseId || 0, chosenDb.name)))
    }
  }, [dispatch])
  
  const { data: dbStatus, refetch: statusRefetch } = useDbEscStatusContext()
  const { saving, refetch } = useEditContext()
  const { refetch: encryptedDbsRefetch } = useEncryptedDatabasesContext()

  const lockHandlers = useDefaultSavedHandlers('Lock Encryption Setting Change')
  const unlockHandlers = useDefaultSavedHandlers('Unlock Encryption Setting Change')
  const rollbackHandlers = useDefaultSavedHandlers('Rollback Encryption Setting Change')
  const finalizeHandlers = useDefaultSavedHandlers('Finalize Encryption Setting Change');
  useEffect(() => {
    [lockHandlers, unlockHandlers, rollbackHandlers, finalizeHandlers].forEach(h => {
      h.onSuccess.merge(statusRefetch)
      h.onFailure.merge(statusRefetch)
      refetch && h.onSuccess.merge(refetch)
    });
    [rollbackHandlers, finalizeHandlers].forEach(h => { h.onSuccess.merge(encryptedDbsRefetch) })
  }, [lockHandlers, unlockHandlers, rollbackHandlers, finalizeHandlers, statusRefetch, refetch, encryptedDbsRefetch])
  rollbackHandlers.onSuccess.merge(useCallback(() => setOpenRollback(false), []))
  
  const save = useSimpleSaveAction()
  const escChange = useRecordContext() as EncryptionSettingChangesResponseDto|undefined
  const unstable = !dbStatus || !escChange || !escChange.isChanged || saving

  const actions = {
    lock:     hasWritePermit && !unstable && dbStatus && dbStatus.upgradeStatus === UpgradeStatus.Unlocked,
    unlock:   hasWritePermit && !unstable && dbStatus && dbStatus.upgradeStatus === UpgradeStatus.Locked,
    rollback: hasWritePermit && !unstable && dbStatus && (
      dbStatus.upgradeStatus === UpgradeStatus.Failed || 
      dbStatus.upgradeStatus === UpgradeStatus.Success),
    finalize: hasWritePermit && !unstable && dbStatus && dbStatus.upgradeStatus === UpgradeStatus.Success,
  }

  const lock = () =>     actions.lock && 
    save({ id: databaseInfo.databaseId, action: 'lock' }, lockHandlers)
  const unlock = () =>   actions.unlock &&
    save({ id: databaseInfo.databaseId, action: 'unlock' }, unlockHandlers)
  const rollback = () => actions.rollback &&
    save({ id: databaseInfo.databaseId, action: 'rollback' }, rollbackHandlers)
  const finalize = () => actions.finalize &&
    save({ id: databaseInfo.databaseId, action: 'finalize' }, finalizeHandlers)

  const alertColor = 
      dbStatus?.upgradeStatus === UpgradeStatus.InProgress ? theme.palette.info
    : dbStatus?.upgradeStatus === UpgradeStatus.Failed ? theme.palette.error
    : dbStatus?.upgradeStatus === UpgradeStatus.Success ? theme.palette.success
    : null

  return (
    <Content 
      pageName="Database Encryption Setting Change" 
      resourceName="Encryption Setting Change" 
      breadcrumbs={[
        ...(options?.breadcrumbs || []), 
        {
          label: 'Database Detail', 
          path: genUrlWithHashid(`/databases/$0/show`, databaseInfo?.databaseId || -1, databaseInfo?.name)
        },
      ]}
      mainActions={
        <>
          <Button size="small" variant="outlined" disabled={!actions.lock}
            onClick={lock}
            startIcon={<LockIcon />}
          >
            Lock
          </Button>
          <Button size="small" variant="outlined" disabled={!actions.unlock}
            onClick={unlock}
            startIcon={<UnlockIcon />}
          >
            Unlock
          </Button>
          <Button size="small" variant="outlined" disabled={!actions.rollback}
            onClick={() => setOpenRollback(true)}
            startIcon={<UndoIcon />}
          >
            Rollback
          </Button>
          <Button size="small" variant="outlined" disabled={!actions.finalize}
            onClick={finalize}
            startIcon={<CheckIcon />}
          >
            Finalize
          </Button>
        </>
      }
    >
      <Grid container spacing={4}>
        <Grid
          item
          xs={12}
        >
          <DatabaseSelectCard preSelectDbId={databaseInfo?.databaseId} onChange={handleChange} showDesc={true} />
        </Grid>
        {alertColor &&
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ bgcolor: alertColor.light, px: 3, py: 2 }}>
              <Typography variant="subtitle2" sx={{ color: alertColor.contrastText }}>
                {alertMessages[dbStatus?.upgradeStatus as keyof typeof alertMessages]}
              </Typography>
            </Card>
          </Grid>
        }
        <Grid item xs={12}>
          <ESCRenameHintContextProvider>
            <ESChangeViewer />
          </ESCRenameHintContextProvider>
        </Grid>
      </Grid>
      {hasWritePermit &&
        <Confirm
          isOpen={openRollback}
          loading={saving}
          title={"Rollback Changes"}
          onConfirm={rollback}
          onClose={() => setOpenRollback(false)}
          confirm="Confirm"
          cancel="Cancel"
          content={(
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ ml: 1 }}
                >
                  {dbStatus?.upgradeStatus === UpgradeStatus.Failed &&
                    <>
                      The latest update to the encryption settings has failed. 
                      <ul>
                        <li>If you would like to retry this update, please <Typography variant="body2" component="span" color="secondary.main">restore the latest backup of your database first</Typography>, then run the OnData Encryption Tool again. Please click on Cancel to exit this dialog. </li>
                        <li>If you would like to abandon this update, please <Typography variant="body2" component="span" color="secondary.main">restore the latest backup of your database first</Typography>, then click on Rollback. Otherwise please click on Cancel to exit this dialog.</li>
                      </ul>
                    </>
                  }
                  {dbStatus?.upgradeStatus === UpgradeStatus.Success &&
                    "The latest update to the encryption settings has succeeded. If you would like to rollback this update, please restore the latest backup of your database first, then click on Rollback. Otherwise please click on Cancel to exit this dialog."
                  }
                </Typography>
              </Box>
            </Box>
          )}
        />}
    </Content>
  )
}

export default DatabaseEncryptionSettingChange;

const alertMessages = {
  [UpgradeStatus.InProgress]: "The changes to the encryption settings are being applied to this database using the OnData Encryption Tool. Please wait for the process to complete.",
  [UpgradeStatus.Failed]: "The latest update to the encryption settings has failed. Please rollback changes and use the error messages from the OnData Encryption Tool to troubleshoot.",
  [UpgradeStatus.Success]: "The latest update to the encryption settings has succeeded. Please verify the changes and decide whether to finalize or rollback this update.",
}


const DbEscStatusContext = createContext<UseGetOneHookValue<DbStatus&RecPatch>>({
  loading: false,
  loaded: false,
  refetch: () => {},
})

const DbEscStatusContextProvider: FC<{ id:number }> = ({ children, id }) => {
  const values = useGetOne<DbStatus&RecPatch>('db-esc-status', id)  
  const { refetch } = values
  const [cachedVals, setCachedVals] = useState(values)
  if (values !== cachedVals) {
    setTimeout(() => 
      setCachedVals(v => {
        const { data: dbStatus_0 } = v
        const { data: dbStatus_1 } = values
        if (dbStatus_0?.databaseId !== dbStatus_1?.databaseId 
         || dbStatus_0?.upgradeStatus !== dbStatus_1?.upgradeStatus
        ) {
          return values
        } else {
          return v
        }
      }))
  }
  
  useEffect(() => {
    const timer = setInterval(() => {
      refetch()
    }, 10 * 1000)

    return () => {
      timer && clearInterval(timer)
    }
  }, [refetch])

  return (
    <DbEscStatusContext.Provider value={cachedVals}>
      {children}
    </DbEscStatusContext.Provider>
  )
}

export const useDbEscStatusContext = () => useContext(DbEscStatusContext);
